import React, { useEffect, useState, useRef } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { ProgressBar } from '@progress/kendo-react-progressbars';
import { Tooltip } from 'antd';
import '@progress/kendo-theme-default/dist/all.css';
import { Link } from 'react-router-dom';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { PDFExport } from '@progress/kendo-react-pdf';
import { PrivateAxios, url } from '../../environment/AxiosInstance';
import EditMiniProjectModal from './EditMiniProjectModal';
import DeleteProjectModal from './DeleteProjectModal';
import AddTaskModal from './ProjectDetails/AddTaskModal';
import { UserAuth } from '../auth/Auth';
import { isURL } from '../../environment/Helper';
import AddMiniProjectModal from './AddMiniProjectModal';
import ProjectMessage from '../../component/ProjectMessage';

// ================== Custom Cell Renderers ================== //
const IDCell = (props) => <td>{props.dataIndex + 1}</td>;

const ProjectNameCell = (props) => (
  <td>
    <Link to={`/project-details/${props.dataItem.id}`} className="text-primary fw-bold">
      {props.dataItem.project_name}
    </Link>
  </td>
);

const ProjectHeadCell = (props, users) => {
  let heads = props.dataItem.project_head_ids;

  if (typeof heads === "string") {
    try {
      heads = JSON.parse(heads);
    } catch {
      heads = [];
    }
  }
  if (!Array.isArray(heads)) heads = heads ? [heads] : [];

  return (
    <td>
      <div className="d-flex align-items-center flex-wrap multi-profile">
        {heads.length > 0 ? (
          heads.map((headId, index) => {
            const user = users.find((u) => u.id === headId);

            return (
              <div className="profile-wrap" key={index}>
                <Tooltip title={user ? user.name : `ID: ${headId}`} placement="top">
                  <div className="exp-avtar bg-exp-yellow">
                    {user?.user_photo != null ? (
                      <img
                        className="prof-img"
                        src={isURL(user?.user_photo) ? user.user_photo : url + user.user_photo}
                        alt="user"
                      />
                    ) : (
                      <span>
                        {user?.name
                          ? user.name.split(' ').length > 1
                            ? user.name.split(' ')[0][0] + user.name.split(' ')[1][0]
                            : user.name.slice(0, 2)
                          : ''}
                      </span>
                    )}
                  </div>
                </Tooltip>
              </div>
            );
          })
        ) : (
          <span className="text-muted">N/A</span>
        )}
      </div>
    </td>
  );
};

const StartDateCell = (props) => {
  const startDate = props.dataItem.start_date ? new Date(props.dataItem.start_date) : null;
  return <td className="fw-medium">{startDate ? startDate.toLocaleDateString() : '-'}</td>;
};

const DeadlineCell = (props) => {
  const deadlineDate = props.dataItem.end_date ? new Date(props.dataItem.end_date) : null;
  return <td className="fw-medium">{deadlineDate ? deadlineDate.toLocaleDateString() : '-'}</td>;
};

const StatusCell = (props) => {
  const { status } = props.dataItem;
  const colorMap = {
    'Pending': 'badge gth-badge-danger-light rounded-pill',
    'In Progress': 'badge gth-badge-success-light rounded-pill',
    'Completed': 'badge gth-badge-info-light rounded-pill',
    'Overdue': 'badge gth-badge-muted-light rounded-pill',
    'On Hold': 'badge gth-badge-warning-light rounded-pill',
  };
  return (
    <td>
      <span className={colorMap[status] || 'badge gth-badge-muted-light'}>
        {status}
      </span>
    </td>
  );
};

const ProgressCell = (props) => {
  const { tasks, progress: progressFromApi, end_date } = props.dataItem;

  let progress = Number.isFinite(progressFromApi)
    ? Number(progressFromApi)
    : (() => {
      if (Array.isArray(tasks) && tasks.length > 0) {
        const completed = tasks.filter(
          (t) =>
            (t.status && String(t.status).toLowerCase() === 'completed') ||
            t.is_done === 1 ||
            t.is_done === true
        ).length;
        return Math.round((completed / tasks.length) * 100);
      }
      return 0;
    })();

  progress = Math.max(0, Math.min(100, progress));

  const deadlineDate = end_date ? new Date(end_date) : null;
  const isPastDeadline = deadlineDate && deadlineDate < new Date() && progress < 100;

  let gradient;
  if (isPastDeadline) {
    gradient = 'linear-gradient(to right, #ffe5e5, #00b386)';
  } else if (progress < 33) {
    gradient = 'linear-gradient(to right, #e9ebee, #f2426e)';
  } else if (progress < 66) {
    gradient = 'linear-gradient(to right, #e4f3ff, #f2426e)';
  } else {
    gradient = 'linear-gradient(to right, #e5f9f0, #0479d6)';
  }

  return (
    <td>
      <div style={{ minWidth: '120px' }}>
        <ProgressBar
          value={progress}
          max={100}
          style={{ height: '14px', borderRadius: '10px' }}
          animation={{ duration: 500 }}
          className="k-progressbar animated-progress-bar"
          progressStyle={{ backgroundImage: gradient }}
        />
      </div>
    </td>
  );
};

const ActionCell = (fetchProjects, openEditModal, handleShowAddPTaskModal, openDeleteModal, messageTaskVisiable) => (props) => {
  const { MatchPermission } = UserAuth();
  
  return (
    <td>
      <div className="d-flex gap-2 align-items-center">
        {MatchPermission(['Add Project', 'Project All']) && (
          <Tooltip title="Add Task">
            <button className="table-action-btn" onClick={() => handleShowAddPTaskModal(props.dataItem)}>
              <i className="fas fa-plus"></i>
            </button>
          </Tooltip>
        )}
        
        {MatchPermission(['Edit Project', 'Project All']) && (
          <Tooltip title="Edit">
            <button className="table-action-btn" onClick={() => openEditModal(props.dataItem)}>
              <i className="fas fa-pen"></i>
            </button>
          </Tooltip>
        )}
        
        {MatchPermission(['Delete Project', 'Project All']) && (
          <Tooltip title="Delete">
            <button className="table-action-btn" onClick={() => openDeleteModal(props.dataItem.id)}>
              <i className="fas fa-trash-alt text-exp-red"></i>
            </button>
          </Tooltip>
        )}
        
        {MatchPermission(['View Project', 'Project All']) && (
          <Tooltip title="Message">
            <button className="table-action-btn" onClick={() => messageTaskVisiable(props.dataItem)}>
              <i className="fas fa-comment-alt text-exp-blue"></i>
            </button>
          </Tooltip>
        )}
      </div>
    </td>
  );
};

// ================== Main Component ================== //
function MiniProjectList({ showAddModal }) {
  const [rows, setRows] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);

  const { User, MatchPermission } = UserAuth();
  const excelExportRef = useRef(null);
  const pdfExportRef = useRef(null);

  const fetchProjects = () => {
    PrivateAxios.get('/project/view')
      .then((res) => {
        if (res.data?.success) setRows(res.data.data || []);
      })
      .catch((err) => console.error('Error fetching projects:', err));
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const openEditModal = (project) => {
    setSelectedProject(project);
    setEditModalVisible(true);
  };

  const handleShowAddPTaskModal = (project) => {
    setSelectedProject(project);
    setTaskModalVisible(true);
  };

  const openDeleteModal = (projectId) => {
    setDeleteProjectId(projectId);
    setDeleteModalVisible(true);
  };

  const exportToExcel = () => excelExportRef.current?.save();
  const exportToPDF = () => pdfExportRef.current?.save();


  //Message Modal Call
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [messageModalData, setMessageModalData] = useState(false);
  const messageTaskVisiable = (data) => {
    console.log(data);

    setMessageModalData(data);
    setMessageModalVisible(true);
  };

  const messageTaskHide = () => {
    setMessageModalVisible(false);
  };


  return (
    <div className="card">
      <div className="card-body p-0">
        <div className="d-flex justify-content-between align-items-center flex-wrap p-3">
          <h5 className="fs-5 my-1">Project List</h5>
          <div className="d-flex gap-2">
            {MatchPermission(['View Project', 'Project All']) && (
              <>
                <button className="btn btn-outline-secondary btn-sm" onClick={exportToExcel}>
                  <i className="fas fa-file-excel me-2" /> Export Excel
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={exportToPDF}>
                  <i className="fas fa-file-pdf me-2" /> Export PDF
                </button>
              </>
            )}
            
            {MatchPermission(['Add Project', 'Project All']) && (
              <button
                className="btn btn-exp-primary btn-sm fw-bold"
                onClick={() => setAddModalVisible(true)}
              >
                <i className="fas fa-plus me-2" /> Add Project
              </button>
            )}
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="p-4 text-center text-muted">
            There are no projects currently displayed. Please add a project.
          </div>
        ) : (
          <ExcelExport ref={excelExportRef} data={rows} fileName="Project_List.xlsx">
            <PDFExport ref={pdfExportRef} paperSize="auto">
              <Grid
                className="kendo-table-border-block rounded-10 overflow-auto"
                data={rows}
                sortable={{ mode: 'multiple' }}
                pageable={{ pageSizes: [5, 10], buttonCount: 3 }}
                resizable
              >
                <GridColumn field="id" title="ID" width="70px" cell={IDCell} />
                <GridColumn field="project_name" title="Project Name" width="350px" cell={ProjectNameCell} />
                <GridColumn
                  field="project_head_ids"
                  title="Project Head"
                  width="220px"
                  cell={(props) => ProjectHeadCell(props, User)}
                />
                <GridColumn field="start_date" title="Start Date" width="180px" cell={StartDateCell} />
                <GridColumn field="end_date" title="End Date" width="180px" cell={DeadlineCell} />
                <GridColumn field="status" title="Status" width="150px" cell={StatusCell} />
                <GridColumn field="progress" title="Progress" width="250px" cell={ProgressCell} />
                <GridColumn
                  field="action"
                  title="Action"
                  width="250px"
                  cell={ActionCell(fetchProjects, openEditModal, handleShowAddPTaskModal, openDeleteModal, messageTaskVisiable)}
                />
              </Grid>
            </PDFExport>
          </ExcelExport>
        )}
      </div>

      {/* Edit Modal */}
      {editModalVisible && selectedProject && (
        <EditMiniProjectModal
          show={editModalVisible}
          handleClose={() => setEditModalVisible(false)}
          projectData={selectedProject}
          onSuccess={() => {
            fetchProjects();
            setEditModalVisible(false);
          }}
        />
      )}

      {/* Delete Modal */}
      {deleteModalVisible && (
        <DeleteProjectModal
          show={deleteModalVisible}
          handleClose={() => setDeleteModalVisible(false)}
          projectId={deleteProjectId}
          onSuccess={() => {
            fetchProjects();
            setDeleteModalVisible(false);
          }}
        />
      )}
      {addModalVisible && (
        <AddMiniProjectModal
          show={addModalVisible}
          handleClose={() => setAddModalVisible(false)}
          onSuccess={() => {
            fetchProjects();
            setAddModalVisible(false);
          }}
        />
      )}
      {/* Add Task Modal */}
      {taskModalVisible && selectedProject && (
        <AddTaskModal
          show={taskModalVisible}
          handleClose={() => setTaskModalVisible(false)}
          projectId={selectedProject.id}
          onSuccess={() => {
            fetchProjects();
            setTaskModalVisible(false);
          }}
        />
      )}
      {/* Project Message Modal */}
      <ProjectMessage
        show={messageModalVisible}
        handleClose={messageTaskHide}
        title="Message Task"
        data={messageModalData}
      />
    </div>
  );
}

export default MiniProjectList;
